import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ALERT_EVENT } from "../utility/toast";
import { displacementMap, supportsRefraction } from "../utility/liquidGlass";
import { spring } from "../lib/springs";

/* Floating alert host. Mounted once in App.js; every Utils.toast(...) call
   surfaces here as a liquid glass toast (.page-alert block in theme.css). */

const ICON = {
  success: "fa-check-circle",
  error: "fa-exclamation-circle",
  warn: "fa-exclamation-triangle",
  info: "fa-info-circle",
};

const AUTO_DISMISS = { success: 4000, info: 4000, warn: 6000, error: 8000 };

// width of the refracting rim, and how far its outer edge bends the backdrop
const BEZEL = 14;
const REFRACTION = 18;

let nextId = 0;

/* One toast. On Chromium it draws a displacement map sized to its own box
   and points its backdrop-filter at it (--lg-filter); everywhere else the
   CSS frosted fallback stands alone. */
const GlassAlert = ({ item, onDismiss }) => {
  const ref = useRef(null);
  const [map, setMap] = useState(null);
  const filterId = "lg-alert-" + item.id;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!supportsRefraction || !el) return undefined;
    let drawn = "";
    const draw = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (drawn === w + "x" + h) return;
      drawn = w + "x" + h;
      const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
      const href = displacementMap(w, h, radius, BEZEL);
      if (href) setMap({ href, w, h });
    };
    draw();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      layout
      role="alert"
      className={"page-alert is-" + item.type + (map ? " is-refracting" : "")}
      style={map ? { "--lg-filter": `url(#${filterId})` } : undefined}
      initial={{ opacity: 0, y: -16, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 0.92,
        transition: spring.moderate.exit,
      }}
      transition={spring.moderate}
    >
      {map && (
        <svg className="page-alert-filter" width="0" height="0" aria-hidden="true">
          <filter
            id={filterId}
            x="0"
            y="0"
            width={map.w}
            height={map.h}
            filterUnits="userSpaceOnUse"
            primitiveUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feImage
              href={map.href}
              x="0"
              y="0"
              width={map.w}
              height={map.h}
              preserveAspectRatio="none"
              result="map"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="map"
              scale={REFRACTION * 2}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </svg>
      )}
      <i
        className={"fa " + (ICON[item.type] || ICON.info) + " page-alert-icon"}
        aria-hidden="true"
      ></i>
      <span className="page-alert-msg">{item.message}</span>
      <button
        type="button"
        className="page-alert-close"
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss message"
      >
        <i className="fa fa-times" aria-hidden="true"></i>
      </button>
    </motion.div>
  );
};

const PageAlert = () => {
  const [items, setItems] = useState([]);

  const remove = useCallback(
    (id) => setItems((prev) => prev.filter((x) => x.id !== id)),
    []
  );

  useEffect(() => {
    const onAlert = (event) => {
      const { type = "info", message = "" } = event.detail || {};
      if (!message) return;
      const id = ++nextId;
      setItems((prev) => [
        // drop an identical message already on screen so it cannot stack
        ...prev.filter((x) => x.message !== String(message)),
        { id, type, message: String(message) },
      ]);
      const ms = AUTO_DISMISS[type] ?? 5000;
      window.setTimeout(() => remove(id), ms);
    };
    window.addEventListener(ALERT_EVENT, onAlert);
    return () => window.removeEventListener(ALERT_EVENT, onAlert);
  }, [remove]);

  // the host stays mounted so AnimatePresence can play the last exit
  return (
    <div className="page-alerts" aria-live="polite">
      <AnimatePresence mode="popLayout" initial={false}>
        {items.map((it) => (
          <GlassAlert key={it.id} item={it} onDismiss={remove} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default PageAlert;
